import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  TextField,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { postSaveOrderAddress } from '../../../../api/orders';
import { updateOrderAddressInRedux } from '../../../../redux/slices/orders';
import { useDispatch, useSelector } from '../../../../redux/store';
import CloseIconButton from '../../../../reusable-components/basic-buttons/CloseIconButton';
import { useSnackbar } from '../../../../reusable-components/snackbar';
import { FIFTY_STATES } from '../../../../utils/dropdowns';

AltAddressDialog.propTypes = {
  open: PropTypes.bool,
  setOpenDialog: PropTypes.func,
  handleCloseDialog: PropTypes.func,
};

export default function AltAddressDialog({ open, setOpenDialog, handleCloseDialog }) {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();

  const { id = '' } = useParams();
  const { orderById } = useSelector((state) => state.orders || {});
  const { facilityAddress, orderAddress } = orderById[id] || {};

  const zipRegex = /^\d{5}(?:[-\s]?\d{4})?$/;
  const phoneNumberRegex = /^(\+?\d{1,4}[\s.-]?)?(\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4})$/;

  const address = orderAddress || facilityAddress;

  const [altAddress, setAltAddress] = useState(null);

  //Redux loads after set state so have to set altAddress initially in a useEffect
  useEffect(() => {
    if (!!address && altAddress === null) {  
      setAltAddress({
        state: { value: address.state, regex: null, hasError: false, required: true },
        street: { value: address.street, regex: null, hasError: false, required: true },
        zip: { value: address.zip, regex: zipRegex, hasError: false, required: true },
        city: { value: address.city, regex: null, hasError: false, required: true },
        phoneNumber: { value: address.phoneNumber, regex: phoneNumberRegex, hasError: false, required: false }
      })
    }
  }, [address, altAddress]);  


  const setAltAddressAsFacilityAddress = () => {
    const facilityAddressInAltStructure = Object.keys(altAddress).reduce((acc, key) => {
      acc[key] = {
        ...altAddress[key],
        value: facilityAddress[key],
      };
      return acc;
    }, {});
    setAltAddress(facilityAddressInAltStructure);
  }

  const validateFields = () => {
    let hasError = false;
    const altAddressCopy = Object.keys(altAddress).reduce((acc, key) => {
      const isFieldRequiredAndEmpty = altAddress[key].required && !altAddress[key].value;
      const isFieldFailingRegex = altAddress[key].regex && altAddress[key].value && !altAddress[key].regex.test(altAddress[key].value);
      const isFieldSameAsFacilityField = altAddress[key].value === facilityAddress[key];
      // fields that are the same as facility address fields will not have an error even if they are not valid
      const hasFieldError = !!(!isFieldSameAsFacilityField && (isFieldRequiredAndEmpty || isFieldFailingRegex));
      acc[key] = {
        ...altAddress[key],
        error: hasFieldError,
      };
      if (hasFieldError) {
        hasError = true;
      }
      return acc;
    }, {});
    setAltAddress(altAddressCopy);
    return hasError;
  }
  const handleCloseDialogInner = () => {
    handleCloseDialog();
    setAltAddress(null);
  }

  const saveDeliveryAddress = () => {
    const hasError = validateFields();
    if (!hasError) {
      const addressToSave = {
        street: altAddress.street.value,
        city: altAddress.city.value,
        state: altAddress.state.value.value || altAddress.state.value,
        zip: altAddress.zip.value,
        phoneNumber: altAddress.phoneNumber.value
      }
      postSaveOrderAddress(id, addressToSave).then(() => {
        dispatch(updateOrderAddressInRedux(id, addressToSave))
        enqueueSnackbar('Delivery address edited successfully', { variant: 'success' });
      }).catch((error) => {
        enqueueSnackbar('Failed to edit delivery address', { variant: 'error' });
        console.error(error);
      }).finally(() => setOpenDialog(false))
    }
  }

  const RequiredFieldWarning = () => <Box component="span" sx={{ fontSize: '0.7rem' }}>*</Box>;


  return (
    <>
      <Dialog open={open} onClose={(event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        handleCloseDialogInner();
      }} maxWidth="md" fullWidth>
        <DialogTitle sx={{ textAlign: 'center', mt: 2, }}>
          Alternative address
          <CloseIconButton onClick={handleCloseDialogInner}/>
        </DialogTitle>
        <DialogContent>
          <Grid justifyContent="space-between" alignItems="center">
            <Grid item xs={12}>
              <Button onClick={setAltAddressAsFacilityAddress} color="info" sx={{ textDecoration: 'underline' }}>Use Facility Address</Button>
              <DialogContentText sx={{ fontWeight: 'bold', mt: 2 }}>Address <RequiredFieldWarning /></DialogContentText>
              <TextField
                name="street"
                placeholder="Address"
                fullWidth
                size="small"
                sx={{
                  mt: 0.5,
                }}
                value={altAddress?.street.value }
                onChange={(e) => {
                  setAltAddress((prevState) => ({
                    ...prevState,
                    street: { ...prevState.street, value: e.target.value },
                  }));
                }}
                error={altAddress?.street.error}
                helperText={
                  altAddress?.street.error && !altAddress?.street.value ? 'Address field must have a value' : ''
                }
                autoComplete="no-autocomplete"
                id="alt-address-street"
              />
            </Grid>
            <Grid item xs={12}>
              <DialogContentText sx={{ fontWeight: 'bold', mt: 2 }}>Phone number</DialogContentText>
              <TextField
                fullWidth
                size="small"
                name="phoneNumber"
                placeholder="Phone number"
                sx={{
                  mt: 0.5,
                }}
                value={altAddress?.phoneNumber.value || ''}
                onChange={(e) => setAltAddress(prevState => ({
                  ...prevState,
                  phoneNumber: {
                    ...prevState.phoneNumber,
                    value: e.target.value,
                  }
                }))}
                error={altAddress?.phoneNumber.error}
                autoComplete="no-autocomplete"
                id="alt-address-phone-number"
                helperText={altAddress?.phoneNumber.error ? 'Input must be a valid phone number' : ''}
              />
            </Grid>
          </Grid>
          <Grid justifyContent="space-between" alignItems="center">
            <Grid item xs={12}>
              <DialogContentText sx={{ fontWeight: 'bold', mt: 2 }}>City <RequiredFieldWarning /></DialogContentText>
              <TextField
                name="city"
                fullWidth
                size="small"
                placeholder="City"
                sx={{ mt: 0.5 }}
                value={altAddress?.city.value }
                onChange={(e) => setAltAddress(prevState => ({
                  ...prevState,
                  city: {
                    ...prevState.city,
                    value: e.target.value,
                  }
                }))}
                error={altAddress?.city.error}
                helperText={altAddress?.city.error && !altAddress?.city.value ? 'City field must have a value' : ''}
                autoComplete="no-autocomplete"
                id="alt-address-city"
              />
            </Grid>
            <Grid container justify="space-around" spacing={2}>
              <Grid item xs={6}>
                <DialogContentText sx={{ fontWeight: 'bold', mt: 2 }}>State <RequiredFieldWarning /></DialogContentText>
                <FormControl fullWidth size="small" sx={{ mt: 0.5 }} error={altAddress?.state.error} variant="outlined">
                  <Autocomplete
                    fullWidth
                    size="small"
                    value={altAddress?.state.value }
                    onChange={(_, newValue) => {
                      setAltAddress(prevState => ({
                        ...prevState,
                        state: {
                          ...prevState.state,
                          value: newValue,
                        }
                      }))
                    }}
                    options={FIFTY_STATES}
                    renderInput={(params) => <TextField {...params} label="Search or select state" />}
                    sx={{
                      '& legend': { display: 'none' },
                      '& fieldset': { top: 0 },
                      '& .MuiFormLabel-root ': { display: 'none' },
                    }}
                  />
                  {altAddress?.state.error && <FormHelperText>State field must have a value</FormHelperText>}
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <DialogContentText sx={{ fontWeight: 'bold', mt: 2 }}>Zip <RequiredFieldWarning /></DialogContentText>
                <TextField
                  name="zip"
                  fullWidth
                  size="small"
                  placeholder="Zip"
                  sx={{ mt: 0.5 }}
                  autoComplete="no-autocomplete"
                  id="alt-address-zip"
                  value={altAddress?.zip.value }
                  onChange={(e) => {
                    setAltAddress(prevState => ({
                      ...prevState,
                      zip: {
                        ...prevState.zip,
                        value: e.target.value,
                      }
                    }))
                  }}
                  error={altAddress?.zip.error}
                  helperText={
                    altAddress?.zip.error && !altAddress?.zip.value ? 'Zip field must have a value' :
                      altAddress?.zip.error ? 'Input must be a valid zip id' : ''
                  }
                />
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="error" size="medium" sx={{ width: 150, mr: 2 }} onClick={handleCloseDialog}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="medium"
            sx={{ width: 150 }}
            onClick={saveDeliveryAddress}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
